import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import { getHttpErrorMessage, getHttpStatus } from '@/lib/httpErrors'
import type { MyBetSummary } from '@/types/miniApp'

/** JA Lab (P0.57, 30/08/2026; P10, 03/09/2026 "JA Lab V2") -- motor real de análisis bajo
 * demanda, exclusivo BLACK. Los tipos reflejan EXACTAMENTE lo que devuelve
 * `app/api/ja_lab.py`/`app/ja_lab/service.py` -- nunca se fabrica un campo que el backend no
 * envía. */

export interface JALabAllowance {
  used: number
  // Sprint "Corrección Final Product Experience V2" (10/09/2026, Sección 5 del encargo):
  // BUG REAL corregido -- `get_allowance` (backend) nunca sabía si el usuario era ADMIN
  // (solo recibía su id, nunca el rol), así que ADMIN veía "0 de 5" igual que cualquier
  // BLACK. Ahora `limit`/`remaining` son `null` cuando `unlimited` es real (excepción de
  // backend, mismo patrón ya vigente en Parlay Lab/SGP Lab).
  limit: number | null
  remaining: number | null
  unlimited: boolean
  resets_at: string
  analyzed_today_fixture_ids: number[]
}

export interface JALabFixtureSummary {
  fixture_id: number
  competition_id: number | null
  competition_name: string | null
  competition_country: string | null
  competition_logo_url: string | null
  home_team: string
  away_team: string
  home_logo: string | null
  away_logo: string | null
  kickoff_utc: string
  coverage_status: 'SUPPORTED' | 'LIMITED'
}

/** JA Lab compete -- now includes logo_url and is_premium flag. Premium7 are
 * highlighted for quick-access but don't restrict the searchable universe. */
export interface JALabCompetitionSummary {
  competition_id: number
  name: string
  country: string | null
  group_label: string
  n_fixtures_today: number
  logo_url: string | null
  is_premium: boolean
}

/** P10 (Sección 2 del encargo): las 6 familias del producto -- siempre las mismas 6, cada
 * fixture marca cuáles están realmente disponibles (`available`), nunca inventa una. */
export type JALabFamily = 'resultado' | 'goles' | 'corners' | 'tarjetas' | 'tiros' | 'jugadores'

export interface JALabFamilyAvailability {
  family: JALabFamily
  label: string
  available: boolean
}

/** P0.57.2 (30/08/2026, Sección 13-18 del encargo): el backend ya humaniza -- nunca se
 * traducen `market`/`selection` crudos en el frontend. `market_label`/`selection_label` ya
 * traen nombres reales de equipo/línea ("Amarante -1.75", nunca "asian_handicap_-1.75 ·
 * home"). `odds_source.reference_text` ya trae el texto final de trazabilidad (Sección 15:
 * bookmaker real / solo proveedor / agregado / sin trazabilidad -- nunca un ID crudo). */
export interface JALabOddsSource {
  bookmaker: string | null
  source: string | null
  fetched_at: string | null
  reference_text: string
}

export interface JALabOpportunity {
  market: string
  market_label: string
  family: JALabFamily | null
  family_label: string | null
  selection: string
  selection_label: string
  side_label: 'LOCAL' | 'VISITANTE' | null
  probability: number | null
  odds: number | null
  fair_odds: number | null
  expected_value: number | null
  edge: number | null
  risk_level: string
  risk_reasons: string[]
  pick_quality_score: number
  confidence_score: number
  factors_favor: string[]
  why_text: string | null
  data_quality_score: number
  coverage_score: number
  // FASE 7 (Sección 7 del encargo, "acuerdo/desacuerdo entre modelos"): 1.0 = acuerdo total
  // entre los modelos reales usados, más bajo = más divergencia real.
  consensus_score: number
  odds_source: JALabOddsSource
  // Sprint "Mi Banca V2" (10/09/2026, Sección 1/5 del encargo): "LA JUGUÉ" por recomendación
  // -- una ejecución de JA Lab puede tener hasta 3 (una por familia), así que a diferencia
  // del resto de productos (1 `my_bet` por generación) aquí vive DENTRO de cada
  // recomendación, emparejado por `market` en el servidor (ver `app/ja_lab/service.py::
  // _attach_my_bets_to_recommendations`).
  my_bet?: MyBetSummary | null
}

export type JALabAnalysisStatus = 'completed' | 'no_value' | 'insufficient_data' | 'odds_unavailable' | 'analysis_failed'

/** FASE 7 (Sección 3 del encargo): Coverage granular real -- sustituye la clasificación
 * binaria de partido "PARCIAL/ANALIZADO". Nunca se mezclan estas 3 métricas: A) cobertura del
 * partido, B) cobertura por familia, C) calidad de la conclusión (ya existente por
 * oportunidad, ver `data_quality_score`/`confidence_score`/`consensus_score` arriba). */
export type JALabCoverageLevel = 'alta' | 'media' | 'baja' | 'no_disponible'

/** Solo la señal de alineaciones trae `status` real (3 estados) -- el resto de señales
 * (forma, H2H, lesiones) siguen siendo `available` binario, ver `coverage_service.py`. */
export type JALabLineupStatus = 'available' | 'pending' | 'not_available'

export interface JALabCoverageSignal {
  label: string
  available: boolean
  status?: JALabLineupStatus
}

export interface JALabMatchCoverage {
  level: JALabCoverageLevel
  score: number
  signals: JALabCoverageSignal[]
}

export interface JALabFamilyCoverage {
  family: JALabFamily
  label: string
  level: JALabCoverageLevel
  reason: string
}

export interface JALabCoverage {
  match: JALabMatchCoverage
  families: JALabFamilyCoverage[]
}

/** P10 (Sección 7 del encargo): "anticipado" -- todavía falta tiempo, el contexto puede
 * cambiar; "actualizado" -- el partido está próximo o ya hay alineaciones oficiales. */
export type JALabTimeAwareness = 'anticipated' | 'updated'

export interface JALabAnalysisResult {
  fixture_id: number
  // Sprint "Mi Banca V2" (10/09/2026, Sección 1/2 del encargo): identifica esta EJECUCIÓN
  // real (`JALabUsage.id`) -- lo que "LA JUGUÉ" necesita para registrar una recomendación
  // concreta de esta ejecución, nunca el id del análisis BASE.
  ja_lab_usage_id: number
  status: JALabAnalysisStatus
  generated_at: string
  odds_snapshot_at: string | null
  probabilities_1x2: { home: number; draw: number; away: number; raw_sum: number } | null
  requested_families: JALabFamily[]
  available_families: JALabFamilyAvailability[]
  // P10 (Sección 5 del encargo): 1-3 recomendaciones EN TOTAL, probabilidad-primero, como
  // mucho una por familia pedida -- reemplaza `best_opportunity`/`other_opportunities`.
  // P.UNA-DECISION (03/09/2026, Sección 4/5 del encargo -- "backend es la autoridad"):
  // `neutral_markets`/`all_markets` ("VER MÁS MERCADOS") se retiraron del envelope real --
  // el backend ya no los envía, nunca se reintroducen aquí como campos fabricados.
  recommendations: JALabOpportunity[]
  markets_analyzed_count: number
  markets_excluded_count: number
  data_notes: string[]
  failure_reason: string | null
  // FASE 7 (Sección 3/7 del encargo): `null` en análisis cacheados de antes de esta fase
  // (nunca inventado) -- ver `JALabCoverage`.
  coverage: JALabCoverage | null
  what_could_change: string[]
  time_awareness: JALabTimeAwareness | null
  time_awareness_reason: string | null
  version: number
  is_reanalysis: boolean
  consumed_new_slot: boolean
  allowance: JALabAllowance
  // Solo presentes al REABRIR (`GET /analysis/{fixture_id}`) -- Sección 7: "hay información
  // nueva disponible".
  current_time_awareness?: JALabTimeAwareness | null
  current_time_awareness_reason?: string | null
  new_context_available?: boolean
}

export interface JALabHistoryItem {
  fixture_id: number
  home_team: string | null
  away_team: string | null
  home_logo: string | null
  away_logo: string | null
  competition_name: string | null
  kickoff_utc: string
  analyzed_at: string
  usage_date: string
  status: JALabAnalysisStatus
  version: number
  requested_families: JALabFamily[]
}

/** Traduce un error de JA Lab a un mensaje humano -- el backend ya envía `detail.message` en
 * español (Sección 26 del encargo: "nunca mostrar traceback"), con fallback al mapeo genérico
 * por status si el body no trae esa forma. */
export function getJALabErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: { message?: string } } } }).response
    const message = response?.data?.detail?.message
    if (message) return message
  }
  return getHttpErrorMessage(getHttpStatus(error))
}

export function getJALabErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: { code?: string } } } }).response
    return response?.data?.detail?.code
  }
  return undefined
}

export function useJALabAllowance(enabled: boolean) {
  return useQuery<JALabAllowance>({
    queryKey: ['ja-lab', 'allowance'],
    queryFn: async () => (await apiClient.get('/api/mini-app/ja-lab/allowance')).data,
    enabled,
    staleTime: 15_000,
  })
}

/** FASE 7 (Sección 2 del encargo): paso 1 del flujo País/Europa → Competición → Partido. */
export function useJALabCompetitions(enabled: boolean) {
  return useQuery<{ competitions: JALabCompetitionSummary[] }>({
    queryKey: ['ja-lab', 'competitions'],
    queryFn: async () => (await apiClient.get('/api/mini-app/ja-lab/competitions')).data,
    enabled,
    staleTime: 60_000,
  })
}

/** FASE 7 (Sección 2 del encargo): paso 2 del flujo -- partidos de hoy de una competición. */
export function useJALabCompetitionFixtures(enabled: boolean, competitionId: number | null) {
  return useQuery<{ fixtures: JALabFixtureSummary[] }>({
    queryKey: ['ja-lab', 'competition-fixtures', competitionId],
    queryFn: async () => (await apiClient.get(`/api/mini-app/ja-lab/competitions/${competitionId}/fixtures`)).data,
    enabled: enabled && competitionId != null,
    staleTime: 60_000,
  })
}

/** JA Lab Discovery (02/10/2026, "buscador GLOBAL de partidos analizables"): filtros
 * combinables entre sí y con `query` -- cada uno opcional. `date` usa formato `YYYY-MM-DD`
 * (día de calendario en la zona de negocio, igual que el resto del backend). */
export interface JALabFixtureFilters {
  team?: string
  country?: string
  competitionId?: number
  date?: string
  /** P0 (07/10/2026): tamaño de página pedido (sin recorte silencioso -- ver `total`/`has_more`). */
  limit?: number
}

/** Respuesta paginada de Discovery: `total` = analizables reales del filtro, `has_more` = hay
 * más allá de lo mostrado (nunca un tope silencioso). */
export interface JALabFixtureSearchResponse {
  fixtures: JALabFixtureSummary[]
  total: number
  offset: number
  limit: number
  has_more: boolean
}

export function useJALabFixtureSearch(enabled: boolean, query: string, filters: JALabFixtureFilters = {}) {
  return useQuery<JALabFixtureSearchResponse>({
    queryKey: ['ja-lab', 'fixtures', query, filters],
    queryFn: async () =>
      (await apiClient.get('/api/mini-app/ja-lab/fixtures', {
        params: {
          ...(query ? { q: query } : {}),
          ...(filters.team ? { team: filters.team } : {}),
          ...(filters.country ? { country: filters.country } : {}),
          ...(filters.competitionId != null ? { competition_id: filters.competitionId } : {}),
          ...(filters.date ? { date: filters.date } : {}),
          ...(filters.limit ? { limit: filters.limit } : {}),
        },
      })).data,
    enabled,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  })
}

/** P10 (Sección 2/14 del encargo): familias reales disponibles para un fixture, ANTES de
 * analizar -- 0 coste. */
export function useJALabFamilies(enabled: boolean, fixtureId: number | null) {
  return useQuery<{ fixture_id: number; families: JALabFamilyAvailability[]; eligible?: boolean; reason?: string | null }>({
    queryKey: ['ja-lab', 'families', fixtureId],
    queryFn: async () => (await apiClient.get(`/api/mini-app/ja-lab/families/${fixtureId}`)).data,
    enabled: enabled && fixtureId != null,
    staleTime: 60_000,
  })
}

/** Sprint "JA Lab: Cierre funcional final" (24/09/2026, Secciones 3/4/5 del encargo): resumen/
 * H2H/forma/lesiones/alineación reales de un fixture ANTES de analizar. */
export interface JALabH2HEntry {
  date: string | null
  home: string
  away: string
  score: string
  competition: string | null
}

export interface JALabInjuryEntry {
  player: string
  team_id: number | null
  status: string
  reason: string | null
}

/** ALINEACIONES (02/10/2026, PRIORITARIO): `Lineup.raw_json` ya se extrae de verdad en el
 * backend (`analysis_engine._real_lineups`) -- nunca se inventa un jugador/posición/
 * formación que el proveedor no trajo. */
export interface JALabLineupPlayer {
  name: string
  number: number | null
  position: string | null
}

export interface JALabLineupEntry {
  team_id: number | null
  is_home: boolean
  formation: string | null
  starters: JALabLineupPlayer[]
  substitutes: JALabLineupPlayer[]
}

/** "Disponible, 0 reportadas" (`available`) vs "todavía no sabemos" (`not_available`) --
 * NUNCA se interpreta ausencia de dato como "sin lesionados" (Sección "LESIONES" del
 * encargo). Mismo vocabulario de 3 estados que `lineup_status` por reutilizar el mismo
 * patrón ya probado (`pending` no aplica a lesiones -- ver `analysis_engine._injuries_
 * status`, solo usa los 2 extremos). */
export type JALabDataAvailability = 'available' | 'pending' | 'not_available'

export interface JALabPreviewSections {
  resumen: boolean
  forma: { available: boolean }
  h2h: { available: boolean }
  injuries: { status: JALabDataAvailability }
  lineups: { status: JALabDataAvailability; available: boolean }
}

export interface JALabMatchPreview {
  fixture_id: number
  resumen: string
  forma: { home: string; away: string }
  h2h: JALabH2HEntry[]
  injuries: JALabInjuryEntry[]
  injuries_status: JALabDataAvailability
  lineup_status: JALabDataAvailability
  lineups: JALabLineupEntry[]
  sections: JALabPreviewSections
}

export function useJALabPreview(enabled: boolean, fixtureId: number | null) {
  return useQuery<JALabMatchPreview>({
    queryKey: ['ja-lab', 'preview', fixtureId],
    queryFn: async () => (await apiClient.get(`/api/mini-app/ja-lab/preview/${fixtureId}`)).data,
    enabled: enabled && fixtureId != null,
    staleTime: 60_000,
  })
}

export function useJALabHistory(enabled: boolean, offset = 0) {
  return useQuery<{ items: JALabHistoryItem[] }>({
    queryKey: ['ja-lab', 'history', offset],
    queryFn: async () => (await apiClient.get('/api/mini-app/ja-lab/history', { params: { offset } })).data,
    enabled,
    staleTime: 15_000,
  })
}

export function useJALabAnalyze() {
  const queryClient = useQueryClient()
  return useMutation<JALabAnalysisResult, unknown, { fixtureId: number; families: JALabFamily[] }>({
    mutationFn: async ({ fixtureId, families }) =>
      (await apiClient.post('/api/mini-app/ja-lab/analyze', { fixture_id: fixtureId, families })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ja-lab', 'allowance'] })
      queryClient.invalidateQueries({ queryKey: ['ja-lab', 'history'] })
    },
  })
}

export function useJALabReopen() {
  return useMutation<JALabAnalysisResult, unknown, { fixtureId: number; version?: number }>({
    mutationFn: async ({ fixtureId, version }) =>
      (await apiClient.get(`/api/mini-app/ja-lab/analysis/${fixtureId}`, { params: version ? { version } : {} })).data,
  })
}
